import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from '@components/atoms/Table';
import { Card } from '@components/atoms/Card';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { useDeleteAlert } from '@services/useDeleteAlert';
import { useNotifications } from '@hooks/useNotifications';
import { AlertItem } from '@services/useGetAlerts';

export interface AlertsTableProps {
    alerts: AlertItem[];
    loading: boolean;
    onRefresh: () => void;
}

export const AlertsTable: FC<AlertsTableProps> = ({ alerts, loading, onRefresh }) => {
    const { t } = useTranslation();
    const { showDefaultNotification } = useNotifications();
    const {
        request: deleteAlert,
        success: deleteSuccess,
        errorMessage: deleteError,
        lastResponseTime: deleteLastResponseTime,
    } = useDeleteAlert();

    const translations = {
        name: t('alerts-table.columns.name'),
        criteria: t('alerts-table.columns.criteria'),
        status: t('alerts-table.columns.status'),
        noAlerts: t('alerts-table.no-alerts'),
        deleteSuccess: t('alerts-table.delete.success'),
        deleteConfirm: t('alerts-table.delete.confirm'),
        loadingText: t('alerts-table.loading'),
        active: t('alerts-table.status.active'),
        inactive: t('alerts-table.status.inactive'),
    };

    // Handle delete response
    useEffect(() => {
        if (deleteLastResponseTime) {
            if (deleteSuccess) {
                showDefaultNotification({ type: 'success', message: translations.deleteSuccess });
                onRefresh();
            } else if (deleteError) {
                showDefaultNotification({ type: 'error', message: deleteError });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deleteLastResponseTime]);

    const handleDelete = (uuid: string) => {
        if (uuid) {
            const confirmed = window.confirm(translations.deleteConfirm);
            if (confirmed) {
                deleteAlert(uuid);
            }
        }
    };

    const formatCriteria = (alert: AlertItem): string => {
        const criteria = alert.search_criteria;
        const parts: string[] = [];

        if (criteria.priceMin !== undefined || criteria.priceMax !== undefined) {
            const priceMin = criteria.priceMin !== undefined ? `${criteria.priceMin}€` : '';
            const priceMax = criteria.priceMax !== undefined ? `${criteria.priceMax}€` : '';
            if (priceMin && priceMax) {
                parts.push(`Preço: ${priceMin} - ${priceMax}`);
            } else if (priceMax) {
                parts.push(`Preço até: ${priceMax}`);
            } else if (priceMin) {
                parts.push(`Preço desde: ${priceMin}`);
            }
        }

        if (criteria.areaMin !== undefined || criteria.areaMax !== undefined) {
            const areaMin = criteria.areaMin !== undefined ? `${criteria.areaMin}m²` : '';
            const areaMax = criteria.areaMax !== undefined ? `${criteria.areaMax}m²` : '';
            if (areaMin && areaMax) {
                parts.push(`Área: ${areaMin} - ${areaMax}`);
            } else if (areaMax) {
                parts.push(`Área até: ${areaMax}`);
            } else if (areaMin) {
                parts.push(`Área desde: ${areaMin}`);
            }
        }

        if (criteria.spaceTypeUuids && criteria.spaceTypeUuids.length > 0) {
            parts.push(`${criteria.spaceTypeUuids.length} tipo(s) de espaço`);
        }

        if (criteria.search) {
            parts.push(`Pesquisa: "${criteria.search}"`);
        }

        return parts.length > 0 ? parts.join(' | ') : '-';
    };

    if (loading) {
        return (
            <Card classes="mb-0">
                <Typography color="muted">{translations.loadingText}</Typography>
            </Card>
        );
    }

    if (alerts.length === 0) {
        return (
            <Card classes="mb-0">
                <Typography color="muted">{translations.noAlerts}</Typography>
            </Card>
        );
    }

    return (
        <Card classes="mb-0">
            <Table
                borderless={true}
                headerCols={[
                    { classes: 'text-left', text: translations.name },
                    { classes: 'text-left', hideOnMobile: true, text: translations.criteria },
                    { classes: 'text-center', text: translations.status },
                    { classes: 'text-left', text: '' },
                ]}
                contentRows={alerts.map((alert) => {
                    return [
                        {
                            classes: 'text-left',
                            content: <Typography fontSize="14">{alert.alert_name}</Typography>,
                        },
                        {
                            classes: 'text-left',
                            content: (
                                <Typography fontSize="14" color="muted">
                                    {formatCriteria(alert)}
                                </Typography>
                            ),
                        },
                        {
                            classes: 'text-center',
                            content: (
                                <Typography fontSize="14">
                                    {alert.is_active ? translations.active : translations.inactive}
                                </Typography>
                            ),
                        },
                        {
                            classes: 'text-end',
                            content: (
                                <Table.Options
                                    options={[
                                        <button key="delete" onClick={() => handleDelete(alert.uuid)}>
                                            <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                                <i className="fa-light fa-trash-can" />
                                            </Typography>
                                        </button>,
                                    ]}
                                />
                            ),
                        },
                    ];
                })}
            />
        </Card>
    );
};
